import { addKnownTag } from '@store/redux/features/inventory/knownData';
import { RootState, useAppDispatch } from '@store/redux/store';
import { useSelector } from 'react-redux';

import { ITagLimitWithDesc } from '@models/Tag.model';
import { AssignTag } from '@pages/Asset/components/Sidebar/components/Tags/components/AssignTag/AssignTag';

import { Icon } from '@blueprintjs/core';
import { BActionIcon } from '@components/BActionIcon/BActionIcon';
import { TagDialogHover } from '@components/TagDialog/TagDialogHover';
import { useHookstate } from '@hookstate/core';
import { Group } from '@mantine/core';
import { primaryColorIndex } from '@services/mantine';
import COLORS_FLAT from '@styles/colors/themes/beam/beam-flat';
import { memo, useCallback, useMemo } from 'react';
import { isNonNullable } from '@utils/isNonNullable';
import { TagListWithDelete } from '../TagListWithDelete';
import { AddMoreButton, BigInputDiv, InputList, TAG_SX } from '../styles';
import { PropsInputSingle, FlattenSingle } from './types';
import {
  addTagsTo,
  calcTagProps,
  disabledTagProps,
  handleSingleDeleteNew,
  onChangeGlobal,
  onChangeSingle,
} from './utils';

type AssignTagsSingleProps = PropsInputSingle & FlattenSingle<'tagsId'>;

function AssignTagsSingle(props: AssignTagsSingleProps) {
  const { index, onlyGroupEdits, setFormV, tagsId, setGlobalFormV } = props;

  const openAssign = useHookstate(false);
  const tagsName = `equipments.${index}.tagsId` as const;

  const dispatch = useAppDispatch();
  const knownTags = useSelector(
    (state: RootState) => state.knownData.knownTags,
  );

  const catTags = useMemo(
    () =>
      tagsId
        .map(tagId => {
          return knownTags[tagId] as ITagLimitWithDesc | undefined;
        })
        .filter(isNonNullable),
    [tagsId, knownTags],
  );
  const noTags = catTags.length === 0;
  const $assignTagsToOne = useCallback(assignTagsToOne, [tagsId]);

  return (
    <InputList className="tags">
      <BigInputDiv className="tags">
        <Group spacing={8} noWrap sx={TAG_SX} data-size={catTags.length}>
          <TagDialogHover
            tags={catTags}
            deleteHandler={handleSingleDeleteNew}
            deleteHandlerArgs={[index, tagsId, setGlobalFormV, setFormV]}
            calcTagProps={onlyGroupEdits ? disabledTagProps : calcTagProps}
          >
            <TagListWithDelete
              tags={catTags}
              deleteHandler={handleSingleDeleteNew}
              deleteHandlerArgs={[index, tagsId, setGlobalFormV, setFormV]}
              calcTagProps={onlyGroupEdits ? disabledTagProps : calcTagProps}
            />
          </TagDialogHover>
          {noTags ? (
            <AddMoreButton
              className="new-icon"
              disabled={onlyGroupEdits}
              onClick={() => {
                openAssign.set(true);
              }}
            >
              <Icon
                icon="plus"
                color={onlyGroupEdits ? COLORS_FLAT.n600 : COLORS_FLAT.b401}
              />
              Assign Tag
            </AddMoreButton>
          ) : (
            <BActionIcon
              disabled={onlyGroupEdits}
              onClick={() => {
                openAssign.set(true);
              }}
              size="lg"
              icon="plus"
              color={primaryColorIndex}
            />
          )}
          <AssignTag
            open={openAssign}
            activeList={catTags}
            onConfirm={$assignTagsToOne}
          />
        </Group>
      </BigInputDiv>
    </InputList>
  );
  function assignTagsToOne($newTags: ITagLimitWithDesc[], done: VoidFunction) {
    dispatch(addKnownTag($newTags));
    const newTags = [...$newTags];
    // Set equipment while keeping it sorted.
    // add
    const newTagsId = addTagsTo(newTags, tagsId, knownTags);

    onChangeSingle(tagsName, newTagsId, setFormV);
    // Add to global
    onChangeGlobal(
      'tagsId',
      oldTags => {
        const oldtagsAsMap = new Map(Object.entries(oldTags));
        newTags.forEach(tag => {
          const oldCount = oldtagsAsMap.get(tag.id);
          const newCount = oldCount?.count ?? 0;
          oldtagsAsMap.set(tag.id, { id: tag.id, count: newCount + 1 });
        });

        return Object.fromEntries(oldtagsAsMap);
      },
      setGlobalFormV,
    );

    done();
  }
}
export default memo(AssignTagsSingle);
